import { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material'
import { Search, ChevronRight } from '@mui/icons-material'

const DataTable = ({
  columns,
  data = [],
  loading = false,
  pagination = true,
  searchable = true,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No data available',
  onRowClick,
  rowKey = 'id',
  initialSortBy,
  initialSortOrder = 'asc',
  stickyHeader = false,
  maxHeight,
  mobileColumns = 3, // Number of columns to show in mobile card view
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState(initialSortBy)
  const [sortOrder, setSortOrder] = useState(initialSortOrder)

  const handleSort = (column) => {
    if (!column.sortable) return
    const isAsc = sortBy === column.field && sortOrder === 'asc'
    setSortOrder(isAsc ? 'desc' : 'asc')
    setSortBy(column.field)
  }

  const filteredData = data.filter((row) => {
    if (!searchTerm) return true
    return columns.some((col) => {
      const value = row[col.field]
      if (value === null || value === undefined) return false
      return String(value).toLowerCase().includes(searchTerm.toLowerCase())
    })
  })

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortBy) return 0
    const aVal = a[sortBy]
    const bVal = b[sortBy]
    if (aVal === bVal) return 0
    if (aVal === null || aVal === undefined) return 1
    if (bVal === null || bVal === undefined) return -1
    const comparison = aVal < bVal ? -1 : 1
    return sortOrder === 'asc' ? comparison : -comparison
  })

  const paginatedData = pagination
    ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedData

  // Get display columns for mobile (exclude action columns)
  const displayColumns = columns.filter(col => col.field !== 'actions').slice(0, mobileColumns)
  const actionColumn = columns.find(col => col.field === 'actions')

  // Mobile Card View
  const MobileCardView = () => (
    <Stack spacing={2}>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : paginatedData.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">{emptyMessage}</Typography>
        </Box>
      ) : (
        paginatedData.map((row, index) => (
          <Card
            key={row[rowKey] || index}
            sx={{
              cursor: onRowClick ? 'pointer' : 'default',
              '&:hover': onRowClick ? { boxShadow: 2 } : {},
            }}
            onClick={() => onRowClick?.(row)}
          >
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  {displayColumns.map((column, colIndex) => {
                    const value = row[column.field]
                    const rendered = column.renderCell
                      ? column.renderCell(value, row, page * rowsPerPage + index)
                      : (value ?? '-')

                    if (colIndex === 0) {
                      return (
                        <Typography key={column.field} variant="subtitle1" fontWeight={600} mb={1}>
                          {rendered || '-'}
                        </Typography>
                      )
                    }

                    return (
                      <Box key={column.field} display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                          {column.headerName}:
                        </Typography>
                        <Typography variant="body2">
                          {rendered || '-'}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {actionColumn && actionColumn.renderCell && (
                    <Box onClick={(e) => e.stopPropagation()}>
                      {actionColumn.renderCell(null, row)}
                    </Box>
                  )}
                  {onRowClick && !actionColumn && (
                    <ChevronRight color="action" />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))
      )}
    </Stack>
  )

  // Desktop Table View
  const DesktopTableView = () => (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: maxHeight,
        boxShadow: 'none',
        border: '1px solid #e2e8f0',
        overflowX: 'auto',
      }}
    >
      <Table stickyHeader={stickyHeader} size="medium">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.field}
                align={column.align || 'left'}
                sx={{
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  width: column.width,
                  minWidth: column.minWidth,
                }}
              >
                {column.sortable ? (
                  <TableSortLabel
                    active={sortBy === column.field}
                    direction={sortBy === column.field ? sortOrder : 'asc'}
                    onClick={() => handleSort(column)}
                  >
                    {column.headerName}
                  </TableSortLabel>
                ) : (
                  column.headerName
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <CircularProgress size={32} />
              </TableCell>
            </TableRow>
          ) : paginatedData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">{emptyMessage}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedData.map((row, index) => (
              <TableRow
                key={row[rowKey] || index}
                hover
                onClick={() => onRowClick?.(row)}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map((column) => (
                  <TableCell key={column.field} align={column.align || 'left'}>
                    {column.renderCell
                      ? column.renderCell(row[column.field], row, page * rowsPerPage + index)
                      : (row[column.field] ?? '-')}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )

  return (
    <Box>
      {searchable && (
        <Box mb={2}>
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setPage(0)
            }}
            fullWidth={isMobile}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: '100%', sm: 250 } }}
          />
        </Box>
      )}

      {isMobile ? <MobileCardView /> : <DesktopTableView />}

      {pagination && !loading && data.length > 0 && (
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={isMobile ? '' : 'Rows per page:'}
          sx={{
            '.MuiTablePagination-selectLabel': {
              display: { xs: 'none', sm: 'block' },
            },
            '.MuiTablePagination-select': {
              display: { xs: 'none', sm: 'block' },
            },
          }}
        />
      )}
    </Box>
  )
}

export default DataTable
